import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchPrompts, createPrompt, updatePrompt as updatePromptAPI, deletePrompt } from '../apis'
import {
  PromptType,
  type IBackendPrompt,
  type IPromptCreateRequest,
  type IPromptUpdateRequest,
} from '../types'
import { useAccountSettingsStore } from './accountSettings'

export interface IPrompt {
  id: string
  name: string
  content: string
  type: 'description' | 'highlight'
  isDefault?: boolean
}

// Convert backend prompt to local prompt format
const convertBackendToLocalPrompt = (backendPrompt: IBackendPrompt): IPrompt => ({
  id: backendPrompt.id,
  name: backendPrompt.title,
  content: backendPrompt.content,
  type: backendPrompt.type as 'description' | 'highlight',
  isDefault: false,
})

// Convert local prompt to backend format
const convertLocalToBackendPrompt = (localPrompt: Omit<IPrompt, 'id'>): IPromptCreateRequest => ({
  title: localPrompt.name,
  content: localPrompt.content,
  type: localPrompt.type === 'description' ? PromptType.DESCRIPTION : PromptType.HIGHLIGHT,
})

export const usePromptsStore = defineStore('prompts', () => {
  const descriptionPrompts = ref<IPrompt[]>([])
  const highlightPrompts = ref<IPrompt[]>([])
  const selectedDescriptionPromptId = ref<string | null>(null)
  const selectedHighlightPromptId = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const { accountId } = useAccountSettingsStore()

  // Load prompts from backend API
  const loadPrompts = async () => {
    isLoading.value = true
    error.value = null

    try {
      // Fetch from backend
      const [backendDescPrompts, backendHighlightPrompts] = await Promise.all([
        fetchPrompts(accountId, PromptType.DESCRIPTION),
        fetchPrompts(accountId, PromptType.HIGHLIGHT),
      ])

      descriptionPrompts.value = backendDescPrompts.map(convertBackendToLocalPrompt)
      highlightPrompts.value = backendHighlightPrompts.map(convertBackendToLocalPrompt)
    } catch (apiError) {
      console.error('Failed to load prompts from API:', apiError)
      error.value = 'Failed to load prompts'
    } finally {
      isLoading.value = false
    }
  }

  const addPrompt = async (prompt: Omit<IPrompt, 'id'>) => {
    isLoading.value = true
    error.value = null

    try {
      // Create prompt via API
      const backendPrompt = convertLocalToBackendPrompt(prompt)
      const createdPrompt = await createPrompt(accountId, backendPrompt)
      const localPrompt = convertBackendToLocalPrompt(createdPrompt)

      if (prompt.type === 'description') {
        descriptionPrompts.value.push(localPrompt)
      } else {
        highlightPrompts.value.push(localPrompt)
      }

      return localPrompt
    } catch (apiError) {
      console.error('Failed to create prompt via API:', apiError)
      error.value = 'Failed to create prompt'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  const removePrompt = async (id: string, type: 'description' | 'highlight') => {
    isLoading.value = true
    error.value = null

    try {
      await deletePrompt(accountId, id)

      // Remove from local state
      if (type === 'description') {
        descriptionPrompts.value = descriptionPrompts.value.filter((p) => p.id !== id)
        if (selectedDescriptionPromptId.value === id) {
          selectedDescriptionPromptId.value = null
        }
      } else {
        highlightPrompts.value = highlightPrompts.value.filter((p) => p.id !== id)
        if (selectedHighlightPromptId.value === id) {
          selectedHighlightPromptId.value = null
        }
      }
    } catch (apiError) {
      console.error('Failed to delete prompt via API:', apiError)
      error.value = 'Failed to delete prompt'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  const updatePrompt = async (id: string, updates: Partial<IPrompt>) => {
    isLoading.value = true
    error.value = null

    try {
      const updateData: IPromptUpdateRequest = {}
      if (updates.name !== undefined) updateData.title = updates.name
      if (updates.content !== undefined) updateData.content = updates.content
      if (updates.type !== undefined) {
        updateData.type =
          updates.type === 'description' ? PromptType.DESCRIPTION : PromptType.HIGHLIGHT
      }

      const updatedBackendPrompt = await updatePromptAPI(accountId, id, updateData)
      const updatedLocalPrompt = convertBackendToLocalPrompt(updatedBackendPrompt)

      // Update local state with API response
      const descPromptIndex = descriptionPrompts.value.findIndex((p) => p.id === id)
      if (descPromptIndex !== -1) {
        descriptionPrompts.value[descPromptIndex] = updatedLocalPrompt
        return
      }

      const highlightPromptIndex = highlightPrompts.value.findIndex((p) => p.id === id)
      if (highlightPromptIndex !== -1) {
        highlightPrompts.value[highlightPromptIndex] = updatedLocalPrompt
      }
    } catch (apiError) {
      console.error('Failed to update prompt via API:', apiError)
      error.value = 'Failed to update prompt'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  const setSelectedDescriptionPrompt = (id: string | null) => {
    selectedDescriptionPromptId.value = id
  }

  const setSelectedHighlightPrompt = (id: string | null) => {
    selectedHighlightPromptId.value = id
  }

  const getSelectedDescriptionPrompt = (): IPrompt | null => {
    if (!selectedDescriptionPromptId.value) return null
    return descriptionPrompts.value.find((p) => p.id === selectedDescriptionPromptId.value) || null
  }

  const getSelectedHighlightPrompt = (): IPrompt | null => {
    if (!selectedHighlightPromptId.value) return null
    return highlightPrompts.value.find((p) => p.id === selectedHighlightPromptId.value) || null
  }

  // Computed properties for easier access
  const allPrompts = computed(() => [...descriptionPrompts.value, ...highlightPrompts.value])
  const hasPrompts = computed(() => allPrompts.value.length > 0)

  // Initialize prompts on store creation
  loadPrompts()

  return {
    // State
    descriptionPrompts,
    highlightPrompts,
    selectedDescriptionPromptId,
    selectedHighlightPromptId,
    isLoading,
    error,

    // Computed
    allPrompts,
    hasPrompts,

    // Actions
    loadPrompts,
    addPrompt,
    removePrompt,
    updatePrompt,
    setSelectedDescriptionPrompt,
    setSelectedHighlightPrompt,
    getSelectedDescriptionPrompt,
    getSelectedHighlightPrompt,
  }
})
